const DEFAULT_IMAGE = "/images/HANDLE-Marketplace-Logo-800px.png";

function trimText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function stripHtml(value) {
  return trimText(String(value || "").replace(/<[^>]*>/g, " "));
}

export function mergeKeywords(...keywordSets) {
  const seen = new Set();
  const merged = [];

  keywordSets.forEach((set) => {
    if (!set) {
      return;
    }

    const tokens = Array.isArray(set) ? set : String(set).split(",");
    tokens.forEach((token) => {
      const normalized = trimText(token);
      const key = normalized.toLowerCase();
      if (normalized && !seen.has(key)) {
        seen.add(key);
        merged.push(normalized);
      }
    });
  });

  return merged;
}

export function normalizeImageUrl(image) {
  const value = trimText(image);
  if (!value) {
    return "";
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (!origin) {
    return value;
  }
  return `${origin}${value.startsWith("/") ? value : `/${value}`}`;
}

export function buildSeoPayload(input = {}) {
  const title = trimText(input.title || "HANDLE Marketplace");
  const description = trimText(
    input.description || "All of your needs in one place."
  );
  const keywords = mergeKeywords(input.keywords).join(", ");
  const image = normalizeImageUrl(input.image || DEFAULT_IMAGE);
  const url =
    trimText(input.url) ||
    (typeof window !== "undefined" ? window.location.href : "");
  const type = trimText(input.type || "website");
  const twitterCard = trimText(input.twitterCard || "summary_large_image");

  return {
    title,
    description,
    keywords,
    image,
    url,
    type,
    twitterCard,
  };
}

function ensureMeta(selector, attributes) {
  const head = document.head;
  let tag = head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    head.appendChild(tag);
  }
  return tag;
}

export function applySeo(payloadInput = {}) {
  if (typeof document === "undefined") {
    return payloadInput;
  }

  const payload = buildSeoPayload(payloadInput);
  document.title = payload.title;

  const description = ensureMeta('meta[name="description"]', {
    name: "description",
  });
  description.setAttribute("content", payload.description);

  const keywords = ensureMeta('meta[name="keywords"]', { name: "keywords" });
  keywords.setAttribute("content", payload.keywords);

  const ogTitle = ensureMeta('meta[property="og:title"]', {
    property: "og:title",
  });
  ogTitle.setAttribute("content", payload.title);

  const ogDescription = ensureMeta('meta[property="og:description"]', {
    property: "og:description",
  });
  ogDescription.setAttribute("content", payload.description);

  const ogType = ensureMeta('meta[property="og:type"]', {
    property: "og:type",
  });
  ogType.setAttribute("content", payload.type);

  const ogUrl = ensureMeta('meta[property="og:url"]', {
    property: "og:url",
  });
  ogUrl.setAttribute("content", payload.url);

  const ogImage = ensureMeta('meta[property="og:image"]', {
    property: "og:image",
  });
  ogImage.setAttribute("content", payload.image);

  const twitterCard = ensureMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
  });
  twitterCard.setAttribute("content", payload.twitterCard);

  const twitterTitle = ensureMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
  });
  twitterTitle.setAttribute("content", payload.title);

  const twitterDescription = ensureMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
  });
  twitterDescription.setAttribute("content", payload.description);

  const twitterImage = ensureMeta('meta[name="twitter:image"]', {
    name: "twitter:image",
  });
  twitterImage.setAttribute("content", payload.image);

  return payload;
}
